/**
 * OcclusionHandler Module
 * 
 * Handles Z-depth calculation and sorting for proper occlusion rendering.
 * Determines which person should be rendered on top when people overlap.
 * 
 * Requirements: 6.1, 6.2
 */

import type { PoseData, PersonData } from '../../types';

/**
 * Calculate average Z-depth for a person using torso landmarks.
 * 
 * Uses shoulders and hips to determine depth. Filters out landmarks
 * with visibility < 0.5 for more accurate depth estimation.
 * 
 * Z-depth interpretation:
 * - Negative values = closer to camera
 * - Positive values = further from camera
 * - Greater Z-depth = render first (back)
 * - Smaller Z-depth = render last (front)
 * 
 * @param pose - Pose data containing landmarks
 * @returns Average Z-depth value, or 0 if no valid torso landmarks
 * 
 * **Validates: Requirements 6.1, 6.2**
 */
export function calculateAverageZDepth(pose: PoseData): number {
  const torsoLandmarks = [
    pose.landmarks.leftShoulder,
    pose.landmarks.rightShoulder,
    pose.landmarks.leftHip,
    pose.landmarks.rightHip,
  ];

  // Filter out low-visibility landmarks (< 0.5)
  const validLandmarks = torsoLandmarks.filter(lm => lm.visibility >= 0.5);

  // Fallback if no valid torso landmarks
  if (validLandmarks.length === 0) {
    return 0;
  }

  // Calculate average Z value
  const sum = validLandmarks.reduce((acc, lm) => acc + lm.z, 0);
  return sum / validLandmarks.length;
}

/**
 * Sort people by Z-depth for correct rendering order.
 * 
 * Sorts in descending order (greater Z-depth first) so that people
 * further from the camera are rendered first, and people closer to
 * the camera are rendered last (appearing on top).
 * 
 * Rendering order:
 * - Person with greatest Z-depth (furthest back) renders first
 * - Person with smallest Z-depth (closest to camera) renders last (on top)
 * 
 * @param people - Array of PersonData to sort
 * @returns New sorted array (does not mutate input)
 * 
 * **Validates: Requirements 6.3, 6.4, 6.5**
 */
export function sortPeopleByDepth(people: PersonData[]): PersonData[] {
  // Create a copy to avoid mutating the input array
  return [...people].sort((a, b) => b.zDepth - a.zDepth);
}
